import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Trash2, Edit3, Check, Clock, RotateCcw, AlarmClockOff, MoreHorizontal, CalendarClock, StickyNote, Send, X, Pencil, Tag, Link2, Copy, LogOut, UserRound } from "lucide-react";
import CopyCode from "@/components/CopyCode";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PRIORITIES, snoozeLabel, categoryChipCls } from "@/lib/constants";
import { useSettings } from "@/context/SettingsContext";

function NotesButton({ task, onSaveNotes }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(task.notes || "");
  useEffect(() => { if (open) setVal(task.notes || ""); }, [open, task.notes]);
  const hasNotes = !!task.notes?.trim();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <HoverCard openDelay={150} closeDelay={60}>
        <HoverCardTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              data-testid={`notes-task-${task.id}`}
              title={hasNotes ? "" : "Add notes"}
              className={`relative h-7 w-7 rounded-full ${hasNotes ? "text-primary" : "text-muted-foreground"} transition-colors`}
            >
              <StickyNote className="w-4 h-4" />
              {hasNotes && <span data-testid={`notes-dot-${task.id}`} className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary ring-2 ring-card" />}
            </Button>
          </PopoverTrigger>
        </HoverCardTrigger>
        {hasNotes && !open && (
          <HoverCardContent align="end" className="w-64" data-testid={`notes-peek-${task.id}`}>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Note</div>
            <p className="text-sm whitespace-pre-wrap break-words">{task.notes}</p>
          </HoverCardContent>
        )}
      </HoverCard>
      <PopoverContent align="end" className="w-72" data-testid={`notes-popover-${task.id}`}>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-muted-foreground">
            Notes for <span className="font-mono">{task.code}</span>
          </div>
          <Textarea
            data-testid={`notes-input-${task.id}`}
            rows={4}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="Add context, links, where you pasted this root…"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" data-testid={`notes-save-${task.id}`} onClick={() => { onSaveNotes(task, val); setOpen(false); }} className="bg-primary hover:bg-[#E85D04] text-white">
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({ task, bucket, selected, onToggleSelect, onQuickSnooze, favoriteLabel, snoozeOptions = [], highlight, onEdit, onDelete, onComplete, onSnooze, onUnsnooze, onReopen, onSaveNotes, onSetPriority, onLeave, members = [], onAssign }) {
  const { openSettings, settings } = useSettings();
  const timer = useRef(null);
  const priority = PRIORITIES[task.priority] || PRIORITIES.medium;
  const done = task.status === "done";
  const cat = (settings?.categories || []).find((c) => c.id === task.category);

  const startPress = () => {
    if (bucket !== "inbox") return;
    timer.current = setTimeout(() => { onQuickSnooze(task); }, 550);
  };
  const cancelPress = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const onContext = (e) => { if (bucket !== "inbox") return; e.preventDefault(); onQuickSnooze(task); };

  if (bucket === "shared") {
    return null;
  }

  return (
    <motion.div
      layout
      id={`taskrow-${task.id}`}
      data-testid="task-row"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.18 }}
      onContextMenu={onContext}
      onTouchStart={startPress}
      onTouchEnd={cancelPress}
      onTouchMove={cancelPress}
      title={bucket === "inbox" ? `Right-click or long-press to snooze ${favoriteLabel}` : undefined}
      className={`group flex items-center gap-2 px-3 sm:px-4 py-2 transition-colors ${
        highlight ? "bg-primary/10" : selected ? "bg-primary/5" : "hover:bg-accent/40"
      }`}
    >
      <span className={`hidden sm:flex items-center shrink-0 transition-opacity ${selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
        <Checkbox data-testid={`select-${task.id}`} checked={selected} onCheckedChange={() => onToggleSelect(task.id)} />
      </span>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`priority-${task.id}`}
            title={`${priority.label} priority — click to change`}
            className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded text-sm font-black leading-none border cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-offset-card transition-all ${priority.cls}`}
          >
            {priority.sym}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-36">
          <DropdownMenuLabel className="text-xs text-muted-foreground">Priority</DropdownMenuLabel>
          {Object.entries(PRIORITIES).map(([k, v]) => (
            <DropdownMenuItem
              key={k}
              data-testid={`set-priority-${k}-${task.id}`}
              onClick={() => onSetPriority(task, k)}
              className="cursor-pointer gap-2"
            >
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded text-xs font-black leading-none border ${v.cls}`}>{v.sym}</span>
              {v.label}
              {task.priority === k && <Check className="w-3.5 h-3.5 ml-auto text-primary" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="shrink-0"><CopyCode code={task.code} size="sm" tone={priority.cls} /></div>
      <span className={`flex-1 min-w-0 truncate text-sm font-medium ${done ? "line-through text-muted-foreground" : ""}`}>
        {task.title}
        {task.shared_by && (
          <span data-testid={`shared-by-${task.id}`} title={`Shared by ${task.shared_by}`} className="ml-2 inline-flex items-center gap-1 align-middle text-xs font-semibold text-primary">
            <Send className="w-3 h-3" /> {task.shared_by}
          </span>
        )}
      </span>

      {cat && (
        <span data-testid={`category-chip-${task.id}`} className={`inline-flex items-center gap-1 shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full border ${categoryChipCls(cat.color)}`}>
          <Tag className="w-3 h-3" /> {cat.name}
        </span>
      )}

      {bucket === "snoozed" && (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 shrink-0">
          <Clock className="w-3.5 h-3.5" /> {snoozeLabel(task.snoozed_until)}
        </span>
      )}

      <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
        {bucket !== "done" && (
          <Button size="icon" data-testid={`complete-task-${task.id}`} onClick={() => onComplete(task)} title="Mark completed" className="h-7 w-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
            <Check className="w-4 h-4" />
          </Button>
        )}
        {bucket === "inbox" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" data-testid={`snooze-task-${task.id}`} title={`Snooze — right-click for ${favoriteLabel}`} className="h-7 w-7 rounded-full text-muted-foreground">
                <Clock className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Snooze</DropdownMenuLabel>
              {snoozeOptions.map((o) => (
                <DropdownMenuItem key={o.key} data-testid={`snooze-${o.key}-${task.id}`} onClick={() => onSnooze(task, o)} className="cursor-pointer">
                  <Clock className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> {o.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={`snooze-edit-${task.id}`} onClick={() => openSettings()} className="cursor-pointer">
                <Pencil className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> Edit snooze options
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {bucket === "snoozed" && (
          <Button size="icon" variant="ghost" data-testid={`unsnooze-task-${task.id}`} onClick={() => onUnsnooze(task)} title="Unsnooze" className="h-7 w-7 rounded-full text-muted-foreground">
            <AlarmClockOff className="w-4 h-4" />
          </Button>
        )}
        {bucket === "done" && (
          <Button size="icon" variant="ghost" data-testid={`reopen-task-${task.id}`} onClick={() => onReopen(task)} title="Reopen" className="h-7 w-7 rounded-full text-muted-foreground">
            <RotateCcw className="w-4 h-4" />
          </Button>
        )}

        <NotesButton task={task} onSaveNotes={onSaveNotes} />

        {task.workspace_id && onAssign && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`assign-${task.id}`} title="Assign" className={`inline-flex items-center gap-1 h-7 max-w-[120px] rounded-full border px-2 text-xs font-semibold shrink-0 transition-colors ${task.assignee ? "border-primary/40 bg-primary/10 text-primary" : "border-dashed border-border text-muted-foreground hover:text-foreground"}`}>
                <UserRound className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{task.assignee_name || "Assign"}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Assign to</DropdownMenuLabel>
              <DropdownMenuItem data-testid={`assign-none-${task.id}`} onClick={() => onAssign(task, null)} className="cursor-pointer">
                <UserRound className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> Unassigned
              </DropdownMenuItem>
              {members.map((m) => (
                <DropdownMenuItem key={m.user_id} data-testid={`assign-${m.user_id}-${task.id}`} onClick={() => onAssign(task, m.user_id)} className="cursor-pointer">
                  <UserRound className="w-3.5 h-3.5 mr-2 text-primary" /> <span className="truncate">{m.name}</span>
                  {task.assignee === m.user_id && <Check className="w-3.5 h-3.5 ml-auto text-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" data-testid={`more-task-${task.id}`} title="More" className="h-7 w-7 rounded-full text-muted-foreground">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem data-testid={`edit-task-${task.id}`} onClick={() => onEdit(task)} className="cursor-pointer">
              <Edit3 className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> Edit
            </DropdownMenuItem>
            {task.link_id && onLeave && (
              <DropdownMenuItem data-testid={`leave-task-${task.id}`} onClick={() => onLeave(task)} className="cursor-pointer">
                <LogOut className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> Leave shared root
              </DropdownMenuItem>
            )}
            <DropdownMenuItem data-testid={`delete-task-${task.id}`} onClick={() => onDelete(task)} className="cursor-pointer text-destructive focus:text-destructive">
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </motion.div>
  );
}

export default function TaskList(props) {
  const { tasks, highlightId, selected } = props;
  return (
    <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden" data-testid="task-list">
      <AnimatePresence mode="popLayout">
        {tasks.map((task) => (
          <Row key={task.id} task={task} {...props} highlight={highlightId === task.id} selected={selected?.has(task.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
}
